import { Fragment, useCallback, useMemo, useState } from "react";
import * as s from "styles/ModalCommonStyled";
import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import StepperComponent, { StepType } from "components/blocks/stepper/Stepper";
import Toggle from "components/atoms/toggle/Toggle";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input from "components/atoms/input/Input";
import TextArea from "components/atoms/input/TextArea";
import BigButton from "components/atoms/buttons/BigButton";
import { Text, ValidateWord } from "components/atoms/text/Text";
import ShowToggle from "components/blocks/modal/toggle/show/ShowToggle";
import AdvancedSettings from "components/blocks/modal/advanced/AdvancedSettings";
import { timeUnitsOptions, timeUnitsOptionsDayHour } from "utils/options";
import { UPDATE_TYPE } from "./AddRuleContainer";
import {
  AlertRuleGroupRecipients,
  AlertRuleRecipients,
  PostAlertRequest,
} from "api/interfaces/alertInterface.interface";
import { SearchRecorder } from "components/element/search/SearchRecorder";
import { SearchUsers } from "components/element/search/SearchUsers";
import {
  ALERT_DETECT_TIME,
  LEVEL_TYPE,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import {
  ProfileAccountInfo,
} from "api/interfaces/accountInterface.interface";
import { SearchAlertAccount } from "components/element/search/SearchAlertAccount";
import DeleteConfirmModal from "components/templates/modal/small/confirm/delete/DeleteConfirmModal";
import SelectComponent from "components/atoms/select/SelectComponent";
import useApiError from "hook/useApiError";
import { useAppSelector } from "redux/hooks";
import { useAuth } from "components/Auth";

interface Props {
  onModal: (type?: string) => void;
  page: number;
  onChangePage: (name: string) => void;
  isEdit?: boolean;
  isView?: boolean;
  steps: Array<StepType>;
  hostName: string;
  data: PostAlertRequest;
  onChangeAlertRuleInfo: (
    type: UPDATE_TYPE,
    value: string | number | boolean | string[] | number[]
  ) => void;
  onChangeAlertRuleCheckInfo: (type: UPDATE_TYPE, checked: boolean) => void;
  onChangeRecipients: (
    value: AlertRuleRecipients[] | AlertRuleGroupRecipients[],
    isGroup?: boolean
  ) => void;
  onClickCreate: () => void;
  onClickSave: () => void;
  onClickDelete: () => void;
  selectedRecorder?: string[];
  selectedAccountList?: string[];
  selectedRecorderGroup?: number[];
  selectedUser: AlertRuleRecipients[];
  selectedGroup: AlertRuleGroupRecipients[];
  alertRuleValidate: Validate;
  alertRuleValidateMsg: ValidateMsg;
  isDeleteConfirm?: boolean;
  userId: string | undefined;
  disabledSave: boolean;
  disabledDelete: boolean;
  onCheckMinMaxValue: (
    type: UPDATE_TYPE,
    value: ALERT_DETECT_TIME | boolean | string | number | string[] | number[]
  ) => void;
}
export function AddRulePresenter(props: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const auth = useAuth();
  const { lang, intlString } = useApiError();

  const [isDeleteConfirm, setIsDeleteConfirm] = useState<boolean>(false);

  const [alertToggle, setAlertToggles] = useState<Record<string, boolean>>({
    system: true,
    disk: false,
    video: false,
  });

  const { isDefault } = props.data;
  const isEditable = useMemo(() => {
    if (!isDefault) {
      return (
        selectedAccount.isAdminGroup ||
        auth.user.email === props.data.preferenceRule.email
      );
    }
    return selectedAccount.isAdminGroup;
  }, [props.data, selectedAccount, auth]);

  const onChangeCollapse = useCallback(
    (name: UPDATE_TYPE) => {
      if (name === UPDATE_TYPE.SYSTEM_ENABLE) {
        setAlertToggles((toggle) => {
          return {
            ...toggle,
            system: !toggle.system,
          };
        });
      }
      if (name === UPDATE_TYPE.DISK_ENABLE) {
        setAlertToggles((toggle) => {
          return {
            ...toggle,
            disk: !toggle.disk,
          };
        });
      }
      if (name === UPDATE_TYPE.VIDEO_ENABLE) {
        setAlertToggles((toggle) => {
          return {
            ...toggle,
            video: !toggle.video,
          };
        });
      }
    },
    [alertToggle]
  );

  const onChangeAlertRuleInfoIntercept = useCallback(
    (
      type: UPDATE_TYPE,
      value: string | number | boolean | string[] | number[]
    ) => {
      if (type === UPDATE_TYPE.SYSTEM_ENABLE) {
        setAlertToggles({
          ...alertToggle,
          system: value as boolean,
        });
      }
      if (type === UPDATE_TYPE.DISK_ENABLE) {
        setAlertToggles({
          ...alertToggle,
          disk: value as boolean,
        });
      }
      if (type === UPDATE_TYPE.VIDEO_ENABLE) {
        setAlertToggles({
          ...alertToggle,
          video: value as boolean,
        });
      }
      props.onChangeAlertRuleInfo(type, value);
    },
    [alertToggle, props]
  );

  // step 1 - rule items
  const ruleItems = useMemo(() => {
    return [
      {
        title: "System Alerts",
        value: props.data.systemAlert.isEnable, // toggle value
        isShow: alertToggle.system,
        name: UPDATE_TYPE.SYSTEM_ENABLE,
        children: (
          <>
            <AdvancedSettings
              label="Not responding more than:"
              min={
                props.data.systemAlert.notResponseType === ALERT_DETECT_TIME.MIN
                  ? 15
                  : 1
              }
              max={99}
              tooltip={intlString(
                lang.label.alertRule.tooltip.recorderNotResponse
              )}
              unit={
                <SelectComponent
                  options={timeUnitsOptions}
                  defaultValue={timeUnitsOptions[0]}
                  value={timeUnitsOptions.find((option) => {
                    return (
                      option.value ===
                      (
                        props?.data?.systemAlert?.notResponseType || ""
                      ).toUpperCase()
                    );
                  })}
                  onChange={(value) => {
                    props.onChangeAlertRuleInfo(
                      UPDATE_TYPE.SYSTEM_NOTRESPONSE_DETECTTIME,
                      value
                    );
                  }}
                  data-testid="alertrule-selects"
                />
              }
              defaultValue={props.data.systemAlert.notResponse}
              defaultChecked={props.data.systemAlert.isCriticalNotResponse}
              onChangeValue={(value) =>
                props.onChangeAlertRuleInfo(
                  UPDATE_TYPE.SYSTEM_NOTRESPONSE,
                  value
                )
              }
              checked={props.data.systemAlert.isCriticalNotResponse}
              onChangeToggle={(checked) =>
                props.onChangeAlertRuleCheckInfo(
                  UPDATE_TYPE.SYSTEM_NOTRESPONSE,
                  checked
                )
              }
              onBlur={() => {
                props.onCheckMinMaxValue(
                  UPDATE_TYPE.SYSTEM_NOTRESPONSE,
                  props.data.systemAlert.notResponseType
                );
              }}
            />

            <AdvancedSettings
              label="Abnormal Restart (occurences per 24h):"
              min={1}
              max={99}
              unit=""
              tooltip={intlString(lang.label.alertRule.tooltip.abnormal)}
              defaultValue={props.data.systemAlert.abnormalStart}
              defaultChecked={props.data.systemAlert.isCriticalAbnormalStart}
              checked={props.data.systemAlert.isCriticalAbnormalStart}
              onChangeValue={(value) =>
                props.onChangeAlertRuleInfo(
                  UPDATE_TYPE.SYSTEM_ABNORMALSTART,
                  value
                )
              }
              onChangeToggle={(e) =>
                props.onChangeAlertRuleCheckInfo(
                  UPDATE_TYPE.SYSTEM_ABNORMALSTART,
                  e
                )
              }
            />

            <AdvancedSettings
              label="Overheated more than:"
              unit="Day(s)"
              min={1}
              max={99}
              tooltip={`${intlString(
                lang.label.alertRule.tooltip.systemOverheat
              )}${String.fromCharCode(0x2103)}`}
              defaultValue={props.data.systemAlert.overHeated}
              defaultChecked={props.data.systemAlert.isCriticalOverheated}
              checked={props.data.systemAlert.isCriticalOverheated}
              onChangeValue={(value) =>
                props.onChangeAlertRuleInfo(
                  UPDATE_TYPE.SYSTEM_OVERHEATED,
                  value
                )
              }
              onChangeToggle={(checked) =>
                props.onChangeAlertRuleCheckInfo(
                  UPDATE_TYPE.SYSTEM_OVERHEATED,
                  checked
                )
              }
            />

            <AdvancedSettings
              label="Recording Failed"
              noCount
              tooltip={intlString(lang.label.alertRule.tooltip.notRecording)}
              defaultChecked={props.data.systemAlert.isCriticalVideoRecording}
              checked={props.data.systemAlert.isCriticalVideoRecording}
              onChangeToggle={(e) =>
                props.onChangeAlertRuleCheckInfo(UPDATE_TYPE.VIDEO_RECORDING, e)
              }
            />
          </>
        ),
      },
      {
        title: "Disk Alerts",
        value: props.data.diskAlert.isEnable, // toggle value
        isShow: alertToggle.disk,
        name: UPDATE_TYPE.DISK_ENABLE,
        children: (
          <>
            <AdvancedSettings
              label="Hardware failure"
              noCount
              tooltip={intlString(lang.label.alertRule.tooltip.hardwareFailure)}
              defaultChecked={props.data.diskAlert.isCriticalHardwareFailure}
              checked={props.data.diskAlert.isCriticalHardwareFailure}
              onChangeToggle={(e) =>
                props.onChangeAlertRuleCheckInfo(
                  UPDATE_TYPE.SYSTEM_HARDWAREFAILURE,
                  e
                )
              }
            />

            <AdvancedSettings
              label="Overheated more than:"
              unit="Day(s)"
              min={1}
              max={99}
              tooltip={`${intlString(
                lang.label.alertRule.tooltip.diskOverheat
              )}${String.fromCharCode(0x2103)}`}
              defaultValue={props.data.diskAlert.overheated}
              defaultChecked={props.data.diskAlert.isCriticalOverheated}
              checked={props.data.diskAlert.isCriticalOverheated}
              onChangeValue={(value) =>
                props.onChangeAlertRuleInfo(UPDATE_TYPE.DISK_OVERHEATED, value)
              }
              onChangeToggle={(checked) =>
                props.onChangeAlertRuleCheckInfo(
                  UPDATE_TYPE.DISK_OVERHEATED,
                  checked
                )
              }
            />

            <AdvancedSettings
              label="BAD Sector Over:"
              unit="%"
              min={1}
              max={99}
              tooltip={intlString(lang.label.alertRule.tooltip.badSector)}
              defaultValue={props.data.diskAlert.badSectorOver}
              defaultChecked={props.data.diskAlert.isCriticalBadSectorOver}
              checked={props.data.diskAlert.isCriticalBadSectorOver}
              onChangeValue={(value) =>
                props.onChangeAlertRuleInfo(UPDATE_TYPE.DISK_BADSECTOR, value)
              }
              onChangeToggle={(e) =>
                props.onChangeAlertRuleCheckInfo(UPDATE_TYPE.DISK_BADSECTOR, e)
              }
            />

            <AdvancedSettings
              label="Storage status checked more than:"
              unit="Hour(s)"
              min={1}
              max={99}
              tooltip={intlString(lang.label.alertRule.tooltip.storageStatus)}
              defaultValue={props.data.diskAlert.storageStatus}
              defaultChecked={props.data.diskAlert.isCriticalStorageStatus}
              checked={props.data.diskAlert.isCriticalStorageStatus}
              onChangeValue={(value) =>
                props.onChangeAlertRuleInfo(
                  UPDATE_TYPE.DISK_STORAGE_STATUS,
                  value
                )
              }
              onChangeToggle={(e) =>
                props.onChangeAlertRuleCheckInfo(
                  UPDATE_TYPE.DISK_STORAGE_STATUS,
                  e
                )
              }
            />

            <AdvancedSettings
              label="Reindexing is taking more than:"
              unit="Hour(s)"
              min={1}
              max={99}
              tooltip={intlString(lang.label.alertRule.tooltip.reIndex)}
              defaultValue={props.data.diskAlert.reindexing}
              defaultChecked={props.data.diskAlert.isCriticalReindexing}
              checked={props.data.diskAlert.isCriticalReindexing}
              onChangeValue={(value) =>
                props.onChangeAlertRuleInfo(UPDATE_TYPE.DISK_REINDEXING, value)
              }
              onChangeToggle={(e) =>
                props.onChangeAlertRuleCheckInfo(UPDATE_TYPE.DISK_REINDEXING, e)
              }
            />

            <AdvancedSettings
              label="Storage space near full:"
              unit="Hour(s)"
              min={1}
              max={99}
              tooltip={intlString(lang.label.alertRule.tooltip.storageFull)}
              defaultValue={props.data.diskAlert.storageSpaceNearFull}
              defaultChecked={
                props.data.diskAlert.isCriticalStorageSpaceNearFull
              }
              checked={props.data.diskAlert.isCriticalStorageSpaceNearFull}
              onChangeValue={(value) =>
                props.onChangeAlertRuleInfo(
                  UPDATE_TYPE.DISK_STORAGE_SPACE_FULL,
                  value
                )
              }
              onChangeToggle={(e) =>
                props.onChangeAlertRuleCheckInfo(
                  UPDATE_TYPE.DISK_STORAGE_SPACE_FULL,
                  e
                )
              }
            />

            <AdvancedSettings
              label="Reserved space not clearing for over:"
              unit="Hour(s)"
              min={1}
              max={99}
              tooltip={intlString(lang.label.alertRule.tooltip.reserveSpace)}
              defaultValue={props.data.diskAlert.reservedSpaceNotClearing}
              defaultChecked={
                props.data.diskAlert.isCriticalReservedSpaceNotClearing
              }
              checked={props.data.diskAlert.isCriticalReservedSpaceNotClearing}
              onChangeValue={(value) =>
                props.onChangeAlertRuleInfo(
                  UPDATE_TYPE.DISK_RESERVE_SPACE_CLEAR,
                  value
                )
              }
              onChangeToggle={(e) =>
                props.onChangeAlertRuleCheckInfo(
                  UPDATE_TYPE.DISK_RESERVE_SPACE_CLEAR,
                  e
                )
              }
            />
          </>
        ),
      },
      {
        title: "Video Alerts",
        value: props.data.videoAlert.isEnable, // toggle value
        isShow: alertToggle.video,
        name: UPDATE_TYPE.VIDEO_ENABLE,
        children: (
          <>
            <AdvancedSettings
              label="Lost connection to camera for more than:"
              min={1}
              max={99}
              tooltip={intlString(lang.label.alertRule.tooltip.loss)}
              defaultValue={props.data.videoAlert.lostConCamera}
              defaultChecked={props.data.videoAlert.isCriticalLostConCamera}
              checked={props.data.videoAlert.isCriticalLostConCamera}
              unit={
                <SelectComponent
                  options={timeUnitsOptionsDayHour}
                  defaultValue={timeUnitsOptionsDayHour[1]}
                  value={timeUnitsOptionsDayHour.find(
                    (option) =>
                      option.value === props.data.videoAlert.lostConCameraType
                  )}
                  onChange={(value) =>
                    props.onChangeAlertRuleInfo(
                      UPDATE_TYPE.VIDEO_LOST_CAMERA_DETECTTIME,
                      value
                    )
                  }
                  data-testid="alertrule-selects-2"
                />
              }
              onChangeValue={(value) =>
                props.onChangeAlertRuleInfo(
                  UPDATE_TYPE.VIDEO_LOST_CAMERA,
                  value
                )
              }
              onChangeToggle={(e) =>
                props.onChangeAlertRuleCheckInfo(
                  UPDATE_TYPE.VIDEO_LOST_CAMERA,
                  e
                )
              }
            />
          </>
        ),
      },
    ];
  }, [
    alertToggle,
    props.onChangeAlertRuleInfo,
    props.data,
    props.onChangeAlertRuleCheckInfo,
  ]);

  return (
    <BigPopupTemplate
      title={
        props.isEdit
          ? props.isView
            ? "View Alert Rule"
            : "Edit Alert Rule"
          : "Add New Rule"
      }
      onModal={props.onModal}
    >
      <StepperComponent
        steps={props.steps}
        activeStep={props.page}
        onClick={props.onChangePage}
        data-testid="alertrule-stepper-component-change-page"
      />

      <s.ModalInnerHalf>
        {/* {pages[props.page]} */}

        <div style={{ display: props.page === 0 ? "block" : "none" }}>
          {selectedAccount.accountLevel === LEVEL_TYPE.EU ? (
            <>
              <s.CheckInput>
                <input
                  type="checkbox"
                  // defaultChecked={true}
                  checked={props.data.recorderRule.autoApplyRecorder}
                  onChange={(e) =>
                    props.onChangeAlertRuleInfo(
                      UPDATE_TYPE.AUTO_APPLY_RECORDER,
                      e.target.checked
                    )
                  }
                  disabled={isDefault}
                  data-testid="alertrule-input-auto-apply-recorder"
                />
                Apply to all current and future recorders.
              </s.CheckInput>

              {!props.data.recorderRule.autoApplyRecorder && (
                <s.ModalTableContainer>
                  <SearchRecorder
                    isTab
                    accountId={selectedAccount.accountId}
                    selectedRow={props.selectedRecorder}
                    selectedGroupRow={props.selectedRecorderGroup}
                    onSelectedRowsChange={(value: string[]) =>
                      props.onChangeAlertRuleInfo(UPDATE_TYPE.RECORDER, value)
                    }
                    onSelectedGroupRowsChange={(value: number[]) =>
                      props.onChangeAlertRuleInfo(
                        UPDATE_TYPE.RECORDER_GROUP,
                        value
                      )
                    }
                    userId={props.userId}
                    isDisabled={isDefault}
                  />
                </s.ModalTableContainer>
              )}

              <ValidateWord data-testid="alertrule-validate-recorder">
                {props.alertRuleValidateMsg.isRecorder}
              </ValidateWord>
            </>
          ) : (
            <>
              <s.CheckInput>
                <input
                  type="checkbox"
                  // defaultChecked={true}
                  checked={props.data.accountRule.autoApplyAccount}
                  onChange={(e) =>
                    props.onChangeAlertRuleInfo(
                      UPDATE_TYPE.AUTO_APPLY_ACCOUNT,
                      e.target.checked
                    )
                  }
                  disabled={isDefault}
                  data-testid="alertrule-input-auto-apply-account"
                />
                Apply to all current and future organization.
              </s.CheckInput>
              {!props.data.accountRule.autoApplyAccount && (
                <s.ModalTableContainer>
                  <SearchAlertAccount
                    accountId={selectedAccount.accountId}
                    selectedAccountId={props.selectedAccountList}
                    onSelectedRowsChange={(value: string[]) => {
                      props.onChangeAlertRuleInfo(UPDATE_TYPE.ACCOUNT, value);
                    }}
                  />
                </s.ModalTableContainer>
              )}

              <ValidateWord data-testid="alertrule-validate-account">
                {props.alertRuleValidateMsg.isAccount}
              </ValidateWord>
            </>
          )}
        </div>
        <div style={{ display: props.page === 1 ? "block" : "none" }}>
          <ShowToggle
            items={ruleItems}
            onToggle={onChangeCollapse}
            onChangeAlertRuleInfo={onChangeAlertRuleInfoIntercept}
            alertRuleValidate={props.alertRuleValidate}
            alertRuleValidateMsg={props.alertRuleValidateMsg}
          />
        </div>
        <div style={{ display: props.page === 2 ? "block" : "none" }}>
          <SearchUsers
            isTab
            accountId={selectedAccount.accountId}
            previlege="alert"
            enableCheckUserList={props.selectedUser}
            enableCheckUserGroupList={props.selectedGroup}
            onSelectedRowsChangeUser={(value: string[]) => {
              props.onChangeAlertRuleInfo(UPDATE_TYPE.USER, value);
            }}
            onChangeEnableCheckUserList={(value: AlertRuleRecipients[]) => {
              props.onChangeRecipients(value);
            }}
            onChangeEnableCheckUserGroup={(
              value: AlertRuleGroupRecipients[]
            ) => {
              props.onChangeRecipients(value, true);
            }}
            isSupportEmail={true}
          />
        </div>
        <div style={{ display: props.page === 3 ? "block" : "none" }}>
          <ModalInput label="Alert Rule Name" essential>
            <Input
              placeholder="Please enter a name"
              value={props.data.preferenceRule.ruleName ?? ""}
              maxLength={100}
              disabled={
                (props.isView !== undefined && props.isView) || isDefault
              }
              onChange={(e) =>
                props.onChangeAlertRuleInfo(
                  UPDATE_TYPE.RULE_NAME,
                  e.target.value
                )
              }
              data-testid="alertrule-input-rule-name"
            />

            <ValidateWord data-testid="alertrule-validate-rule-name">
              {props.alertRuleValidateMsg.isRuleName}
            </ValidateWord>
          </ModalInput>
          <ModalInput label="Host">
            <Input
              disabled
              // value 고정
              value={props.data.preferenceRule.host ?? ""}
            />
          </ModalInput>
          <ModalInput label="Description">
            <TextArea
              placeholder="Please enter a description"
              value={props.data.preferenceRule.description ?? ""}
              disabled={
                (props.isView !== undefined && props.isView) || isDefault
              }
              onChange={(e) =>
                props.onChangeAlertRuleInfo(UPDATE_TYPE.DESC, e.target.value)
              }
              data-testid="alertrule-textarea-description"
            />
          </ModalInput>
          {props.isEdit && (
            <s.RowModalInput>
              <Text bold>Enable</Text>
              <Toggle
                isToggled={props.data.isEnable}
                checked={props.data.isEnable}
                onChange={(checked) =>
                  props.onChangeAlertRuleInfo(UPDATE_TYPE.ENABLE, checked)
                }
                dataTestId="alertrule-input-enable-toggle"
                disabled={
                  props.isView !== undefined && props.isView
                  // || isDefault
                }
              />
            </s.RowModalInput>
          )}
        </div>
        {props.isEdit ? (
          props.isView ? (
            <s.ButtonWrapper>
              {props.page !== 0 && (
                <BigButton
                  buttonType="cancel"
                  label="Previous"
                  onClickBtn={() => props.onChangePage("previous")}
                  data-testid="alertrule-button-change-page"
                />
              )}

              {props.page !== 3 ? (
                <BigButton
                  buttonType="cancel"
                  label="Next"
                  onClickBtn={() => props.onChangePage("next")}
                  data-testid="alertrule-button-change-page-2"
                />
              ) : (
                <Fragment />
              )}
            </s.ButtonWrapper>
          ) : (
            <s.DeleteButtonWrapper>
              {!isDefault && isEditable && (
                <BigButton
                  buttonType="warning"
                  label="Delete"
                  onClickBtn={() => setIsDeleteConfirm(true)}
                  disabled={
                    props.disabledDelete ||
                    (props.isView !== undefined && props.isView)
                  }
                  data-testid="alertrule-button-set-is-delete-confirm"
                />
              )}

              <s.ButtonWrapper>
                {props.page !== 0 && (
                  <BigButton
                    buttonType="cancel"
                    label="Previous"
                    onClickBtn={() => props.onChangePage("previous")}
                    data-testid="alertrule-button-change-page-3"
                  />
                )}

                {props.page !== 3 ? (
                  <BigButton
                    buttonType="cancel"
                    label="Next"
                    onClickBtn={() => props.onChangePage("next")}
                    data-testid="alertrule-button-change-page-4"
                  />
                ) : isEditable ? (
                  <BigButton
                    label="Save"
                    onClickBtn={props.onClickSave}
                    delay
                    disabled={
                      props.disabledSave ||
                      (props.isView !== undefined && props.isView)
                    }
                    data-testid="alertrule-button-click-save"
                  />
                ) : (
                  <></>
                )}
              </s.ButtonWrapper>
            </s.DeleteButtonWrapper>
          )
        ) : (
          <s.ButtonWrapper>
            {props.page !== 0 && (
              <BigButton
                buttonType="cancel"
                label="Previous"
                onClickBtn={() => props.onChangePage("previous")}
                data-testid="alertrule-button-change-page-5"
              />
            )}

            {isEditable && props.page === 3 ? (
              <BigButton
                label="Save"
                onClickBtn={props.onClickCreate}
                delay
                disabled={props.disabledSave}
                data-testid="alertrule-button-click-create"
              />
            ) : (
              <BigButton
                buttonType="cancel"
                label="Next"
                onClickBtn={() => props.onChangePage("next")}
                data-testid="alertrule-button-change-page-6"
              />
            )}
          </s.ButtonWrapper>
        )}

        {props.isEdit && isDeleteConfirm && (
          <DeleteConfirmModal
            label={`rule`}
            onDelete={() => setIsDeleteConfirm(false)}
            onConfirmDelete={() => {
              props.onClickDelete();
              setIsDeleteConfirm(false);
            }}
          />
        )}
      </s.ModalInnerHalf>
    </BigPopupTemplate>
  );
}
